<script setup lang="ts">

import type {ExtendedNuxtError} from "~/types/Errors";

const props = defineProps<{error: ExtendedNuxtError}>();

const title = computed(() => {
  if(props.error?.data && props.error.data.title) {
    return props.error.data.title;
  }
  return props.error?.statusCode;
});

const errorMessage = computed(() => {
  switch(props.error?.statusCode) {
    case 404:
      return 'You already know what this means...';
    case 500:
      return 'Well, this is awkward...';
    default:
      return props.error?.statusMessage;
  }
});

const errorDescription = computed(() => {
  if(props.error?.data && props.error.data.description) {
    return props.error.data.description;
  }else if(props.error?.statusCode === 500) {
    return 'Something broke on our end. Don’t stress—it happens to the best of us.<br/><br/>Why not take a moment, grab a coffee, while we sort this out.'
  }

  return null;
});

const showHomeUrl = computed(() => {
  return props.error?.statusCode === 404 || props.error?.statusCode === 500 || props.error?.statusCode === 403;
});

const showRefreshUrl = computed(() => {
  return !(props.error?.statusCode === 404 || props.error?.statusCode === 429 || props.error?.statusCode === 403);
});

const showAdditionalLink = computed(() => Boolean(props.error?.data?.additionalLink));

const handleError = () => clearError({ redirect: '/' });
const refresh = () => clearError();
</script>

<template>
  <div class="wad4-app">
    <VitePwaManifest/>
    <page-app-header/>
    <div class="wad4-app-page wad4-error-page">
      <client-only>
        <div class="wad4-error-page__title">{{ title }}</div>
        <div class="wad4-error-page__subtitle">{{ errorMessage }}</div>
        <div v-if="errorDescription" class="wad4-error-page__description" v-html="errorDescription"></div>
        <div class="wad4-error-page__buttons">
          <button v-if="showRefreshUrl" class="wad4-button-secondary-grey wad4-button--width-full" @click="refresh"><span class="wad4-button__text">Refresh</span></button>
          <button v-if="showHomeUrl" class="wad4-button-secondary-grey wad4-button--width-full" @click="handleError"><span class="wad4-button__text">Return Home</span></button>
        </div>
        <div v-if="showAdditionalLink" class="wad4-error-page__additional-info">Additional information <a :href="props.error.data.additionalLink">Link</a></div>
      </client-only>
    </div>
  </div>
</template>
<style lang="scss">
.wad4-error-page {
  display: flex;
  padding: 0 $wad4-spacing-6xl;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: $wad4-spacing-xl;
  flex: 1 0 0;
  align-self: stretch;
  color: $wad4-c-text-text-brand-primary;

  &__title {
    text-align: center;
    @include display-size-mixin('xl');
  }

  &__subtitle {
    text-align: center;
    @include display-size-mixin('xs');
  }

  &__description {
    text-align: center;
    @include text-size-mixin('md');
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $wad4-spacing-md;
    align-self: stretch;

    & .wad4-button--width-full {
      max-width: 280px;
    }
  }

  &__additional-info {
    color: $wad4-c-text-text-primary;
    display: flex;
    height: 18px;
    justify-content: center;
    align-items: center;
    gap: 6px;

    & a {
      color: $wad4-c-text-text-primary;
      text-decoration: underline;
    }
  }
}
</style>
